import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { useAuthStore } from './store';

// Lazy loading ile sayfaları yükle
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const DailyChecks = lazy(() => import('./pages/DailyChecks'));
const WeeklyChecks = lazy(() => import('./pages/WeeklyChecks').then(module => ({ default: module.WeeklyChecks })));
const Reports = lazy(() => import('./pages/Reports').then(module => ({ default: module.Reports })));
const Settings = lazy(() => import('./pages/Settings').then(module => ({ default: module.Settings })));
const Messages = lazy(() => import('./pages/Messages'));
const Facilities = lazy(() => import('./pages/Facilities'));
const BagTV = lazy(() => import('./pages/BagTV'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const DataViewer = lazy(() => import('./pages/DataViewer'));
const Approvals = lazy(() => import('./pages/Approvals'));
const CompletedWorks = lazy(() => import('./pages/CompletedWorks'));
const YBSWorkProgram = lazy(() => import('./pages/YBSWorkProgram'));
const YBSApprovals = lazy(() => import('./pages/YBSApprovals'));
const BagKart = lazy(() => import('./pages/BagKart'));
const BagKartBusinessInfo = lazy(() => import('./pages/BagKartBusinessInfo'));

// Loading component
const PageLoader = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
);

// Sayfa yetki kontrolü için mapping
const pagePermissions = {
  '/': 'Ana Sayfa',
  '/facilities': 'Tesisler',
  '/daily-checks': 'Günlük İş Programı',
  '/haftalik': 'Toplam Yapılan İşler',
  '/reports': 'Raporlar',
  '/messages': 'Mesaj Yönetimi',
  '/bagtv': 'BağTV Yönetim',
  '/data-control': 'Veri Kontrol',
  '/approvals': 'Onay Yönetimi',
  '/completed-works': 'Yapılan İşler',
  '/settings': 'Ayarlar',
  '/user-management': 'Kullanıcı Yönetimi',
  '/ybs-work-program': 'YBS İş Programı',
  '/ybs-approvals': 'YBS Onay Ekranları',
  '/bagkart': 'BagKart Yönetimi',
  '/bagkart-business-info': 'BağKart Üye İşyeri Bilgileri',
  '/monthly-checks': 'Aylık Kontroller',
  '/yearly-checks': 'Yıllık Kontroller'
};

// PrivateRoute bileşeni
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, checkAuth } = useAuthStore();
  const checkAuthResult = checkAuth();
  const location = useLocation();
  
  // Sadece development'ta debug log
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    console.log('🔍 PrivateRoute Debug:', {
      isAuthenticated,
      hasUser: !!useAuthStore.getState().user,
      checkAuthResult,
      hasUserId: useAuthStore.getState().user?.id,
      hasUsername: useAuthStore.getState().user?.username,
      hasRole: useAuthStore.getState().user?.role,
      hasPermissions: useAuthStore.getState().user?.permissions?.length || 0
    });
  }
  
  if (!checkAuthResult) {
    return <Navigate to="/login" replace />;
  }

  // Sayfa yetki kontrolü
  const currentPath = location.pathname;
  const requiredPermission = pagePermissions[currentPath];
  
  if (requiredPermission) {
    const userPermissions = useAuthStore.getState().user?.permissions || [];
    const userRole = useAuthStore.getState().user?.role;
    
    // Admin kullanıcılar tüm sayfalara erişebilir
    if (userRole === 'admin') {
      console.log(`✅ Admin kullanıcı - ${requiredPermission} sayfasına erişim izni`);
    }
    // User kullanıcılar sadece kendi yetkilerine sahip sayfalara erişebilir
    else if (!userPermissions.includes(requiredPermission)) {
      console.log(`❌ Yetki yok - ${requiredPermission} yetkisi gerekli`);
      return <Navigate to="/" replace />;
    } else {
      console.log(`✅ Yetki var - ${requiredPermission} yetkisi mevcut`);
    }
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <div className="h-screen overflow-hidden">
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="*"
            element={
              <PrivateRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/facilities" element={<Facilities />} />
                      <Route path="/daily-checks" element={<DailyChecks />} />
                      <Route path="/haftalik" element={<WeeklyChecks />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/messages" element={<Messages />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/bagtv" element={<BagTV />} />
                      <Route path="/user-management" element={<UserManagement />} />
                      <Route path="/data-control" element={<DataViewer />} />
                      <Route path="/approvals" element={<Approvals />} />
                      <Route path="/completed-works" element={<CompletedWorks />} />
                      <Route path="/ybs-work-program" element={<YBSWorkProgram />} />
                      <Route path="/ybs-approvals" element={<YBSApprovals />} />
                      <Route path="/bagkart" element={<BagKart />} />
                      <Route path="/bagkart-business-info" element={<BagKartBusinessInfo />} />
                    </Routes>
                  </Suspense>
                </Layout>
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </div>
  );
}

export default App; 